import { ApiService } from './api';

export interface DomainSearchResult {
  domain: string;
  available: boolean;
  price: number | null;
}

export interface DomainSearchResponse {
  domains: DomainSearchResult[];
  apiUnavailable: boolean;
}

export interface CreateOrderDomain {
  domain: string;
  price: number;
}

export interface CreateOrderAccount {
  username: string;
  domain: string;
  personaFirstName: string;
  personaLastName: string;
}

export interface CreateOrderRequest {
  currency?: string;
  domains: CreateOrderDomain[];
  accounts: CreateOrderAccount[];
}

export interface CreateOrderResponse {
  checkoutUrl: string;
}

export interface DfyOrder {
  id: string;
  workspaceId: number;
  userId: string;
  stripePaymentIntentId: string | null;
  stripeSessionId: string | null;
  totalAmountCents: number;
  domainCount: number;
  accountCount: number;
  status: string;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DfyDomain {
  id: string;
  orderId: string;
  workspaceId: number;
  domain: string;
  registrarOrderId: string | null;
  registrarDomainId: string | null;
  priceCents: number;
  status: string;
  googleCustomerId: string | null;
  googleSubscriptionId: string | null;
  dnsConfigured: boolean;
  domainVerified: boolean;
  errorMessage: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProvisioningStep {
  id: string;
  domainId: string | null;
  accountId: string | null;
  stepName: string;
  status: 'pending' | 'in_progress' | 'success' | 'failed' | 'skipped';
  startedAt: string | null;
  completedAt: string | null;
  attemptCount: number;
  maxAttempts: number;
  nextRetryAt: string | null;
  errorMessage: string | null;
  errorCode: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DfyEmailAccount {
  id: string;
  orderId: string;
  domainId: string;
  workspaceId: number;
  emailAddress: string;
  username: string;
  personaFirstName: string;
  personaLastName: string;
  priceCents: number;
  googleUserId: string | null;
  status: string;
  errorMessage: string | null;
  emailAccountId: string | null;
  createdAt: string;
  updatedAt: string;
  provisioningSteps?: ProvisioningStep[];
}

export interface OrderStatusResponse {
  order: DfyOrder;
  domains: DfyDomain[];
  accounts: DfyEmailAccount[];
}

export interface ListOrdersResponse {
  orders: DfyOrder[];
}

class DfyService extends ApiService {
  constructor() {
    super();
  }

  async getPricing(currency: string): Promise<{ currency: string; symbol: string; domainPriceCents: number; accountPriceCents: number }> {
    return this.get(`/api/dfy/pricing?currency=${currency}`);
  }

  async searchDomains(query: string, tlds: string[] = ['com', 'org'], currency: string = 'usd'): Promise<DomainSearchResponse> {
    const params = new URLSearchParams({ query, currency });
    tlds.forEach(tld => params.append('tld', tld));
    return this.get<DomainSearchResponse>(`/api/dfy/domains/search?${params.toString()}`);
  }

  async createOrder(data: CreateOrderRequest): Promise<CreateOrderResponse> {
    return this.post<CreateOrderResponse>('/api/dfy/orders', data);
  }

  async getOrderStatus(orderId: string): Promise<OrderStatusResponse> {
    return this.get<OrderStatusResponse>(`/api/dfy/orders/${orderId}`);
  }

  async listOrders(): Promise<ListOrdersResponse> {
    return this.get<ListOrdersResponse>('/api/dfy/orders');
  }

  async processOrder(orderId: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/api/dfy/orders/${orderId}/process`);
  }

  async getOrderBySessionId(sessionId: string): Promise<OrderStatusResponse> {
    return this.get<OrderStatusResponse>(`/api/dfy/orders/by-session/${sessionId}`);
  }

  async cancelOrder(orderId: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>(`/api/dfy/orders/${orderId}/cancel`);
  }

  async listAccounts(): Promise<{ accounts: DfyEmailAccount[] }> {
    return this.get<{ accounts: DfyEmailAccount[] }>('/api/dfy/accounts');
  }

  async deleteAccount(accountId: string): Promise<{ success: boolean }> {
    return this.delete<{ success: boolean }>(`/api/dfy/accounts/${accountId}`);
  }
}

export const dfyService = new DfyService();
